import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { employerAPI, employeeAPI } from '../services/api';
import { useAuth } from './AuthContext';

const MessageContext = createContext();

export const MessageProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const isEmployer = user?.account_type === 'employer';
  const pollIntervalRef = useRef(null);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    
    try {
      if (isEmployer) {
        // Use dedicated unread count endpoint
        const res = await employerAPI.getUnreadCount();
        setUnreadCount(res.data?.unread_count || 0);
      } else {
        // Use dedicated unread count endpoint for employees
        const res = await employeeAPI.getUnreadCount();
        setUnreadCount(res.data?.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to refresh unread count:', err);
    }
  }, [user, isEmployer]);

  // Poll for unread messages every 3 seconds
  useEffect(() => {
    if (!user) return;
    
    // Initial fetch
    refreshUnreadCount();
    
    // Set up polling every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      refreshUnreadCount();
    }, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user, refreshUnreadCount]);

  // Mark a specific message as read and refresh count
  const markAsRead = useCallback(async (messageId) => {
    // The backend marks it as read when fetching the thread
    // Just refresh the count
    await refreshUnreadCount();
  }, [refreshUnreadCount]);

  return (
    <MessageContext.Provider value={{ unreadCount, refreshUnreadCount, markAsRead, setUnreadCount }}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessages = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return context;
};
